import { Injectable, Injector } from '@angular/core';

import { StatsigService } from '@services/statsig.service';
import { DashboardSettings } from '@services/dashboard-settings';
import { Session } from '@services/session';

import { isPublicUrlAccess } from '@lib/public-url-access';

const FEDEX_EXPERIENCE_ENABLED = 'fedex_experience_enabled';

// define an enum if feature gate key is used anywhere else in the app
export enum FLEET_VIEW_FEATURE_FLAGS {
  FE_FV_FIT_TO_MAP_AND_CENTER = 'fe_fv_fit_to_map_and_center',
  FV_MAP_CONTEXT_MENU_SLB_LOADS_ENABLED = 'fleet-view-map-context-menu-slb-loads-enabled',
  FV_DRIVERS_LIVE_TAB_LOADS_ENABLED = 'fleet-view-drivers-live-tab-loads-enabled',
  FV_DRIVERS_DEDICATED_LOADS_TAB_ENABLED = 'fleet-view-drivers-dedicated-loads-tab-enabled',
  FV_VEHICLES_DEDICATED_LOADS_TAB_ENABLED = 'fleet-view-vehicles-dedicated-loads-tab-enabled',
  FV_LIVE_SHARE_DESTINATION_ENABLED= 'fv-live-share-destination-enabled',
  FE_FV_FOLLOW_MODE_LOGGING = 'fe-fv-follow-mode-logging',
  FE_FV_HARDWIRED_CHANGES_ENABLED='fe_fv_hardwire_changes_enabled',
  FE_FV_GEOFENCES_DISABLED = 'fe-fv-geofences-disabled',
  FE_FV_MAP_MARKER_CLUSTERING_DISABLED = 'fe-fv-map-marker-clustering-disabled',
  FE_FV_FEDEX_VEHICLE_PAGINATED_API_ENABLED = 'fe-fv-fedex-vehicle-paginated-api-enabled',
  FE_FV_FEDEX_DRIVER_PAGINATED_API_ENABLED = 'fe-fv-fedex-driver-paginated-api-enabled',
  FE_FV_FEDEX_ASSET_PAGINATED_API_ENABLED = 'fe-fv-fedex-asset-paginated-api-enabled',
  FE_FV_HISTORY_ROUTES_FALLBACK_FOR_NO_BREADCRUMBS = 'fe-fv-history-routes-fallback-for-no-breadcrumbs',
  FE_FV_HOS_CLOCK_DISABLED = 'fe-fv-hos-clock-disabled',
  FE_FV_DRIVER_COMPANY_ID_SEARCH_ENABLED = 'fe_fv_driver_company_id_search_enabled',
  FE_FV_DRIVER_SORT_OPTION_DISABLED = 'fe_fv_driver_sort_option_disabled',
  AT_TRIP_IMAGES_ENABLED = 'at-trip-images-enabled',
  FE_FV_MARKERS_MAP_LAYERS_AND_LEGENDS = 'fe-fv-markers-map-layers-legends',
  FE_FV_SHOW_GROUPS_ON_ENTITY_DETAIL_VIEW = 'fe-fv-show-groups-on-entity-detail-view',
  FE_DRIVER_PROFILE_HOS_CARD_ENABLED = 'fe-driver-profile-hos-card-enabled',
  ASSET_TRACKING_GEOFENCE_BULK_STATUS_UPDATE_ENABLED = 'asset-tracking-geofence-bulk-status-update-enabled',
  FE_MAP_INSTANCE_OPTIMIZATION_ENABLED = 'fe-map-instance-optimization-enabled',
  FE_FV_ENABLE_GOOGLE_MAPS_COST_ANALYSIS_LOGS = 'fe-fv-enable-google-maps-cost-analysis-logs',
  FE_FV_TRIP_IMAGES_PHASE2_ENABLED ='fe-fv-trip-images-phase2-enabled',
  FE_FV_TRIP_HISTORY_CALENDAR_INDICATORS_ENABLED = 'fe-fv-trip-history-calendar-indicators-enabled',
  FE_FV_DISABLE_LIST_NEARBY_ENTITIES = 'fe-fv-disable-list-nearby-entities',
  FE_FV_SHORYUKEN_OMIT_OUT_OF_ORDER_PUSHER_UPDATES = 'fe-fv-shoryuken-omit-out-of-order-pusher-updates',
  FE_FV_VEHICLE_DRIVER_HISTORY_ZERO_TRIP = 'fe-fv-vehicle-driver-history-zero-trip',
  AT_FLEETVIEW_V2_M1_ENABLED = 'at-fleetview-v2-m1-enabled',
  AT_CUSTOM_GEOFENCE_CATEGORIES_ENABLED = 'at-custom-geofence-categories-enabled',
}

@Injectable({ providedIn: 'root' })
export class FleetViewFeatureFlagService {
  private isFedexExperienceEnabled: boolean = false;
  private dashboardSettings;
  private session;
  constructor(
    private statsigService: StatsigService,
    private injector: Injector,
  ) {
    if (!isPublicUrlAccess()) {
      this.dashboardSettings = this.injector.get(DashboardSettings);
      this.session = this.injector.get(Session);
      this.isFedexExperienceEnabled = this.dashboardSettings.getKey(FEDEX_EXPERIENCE_ENABLED);
    }
  }

  public isFVFitToMapAndCenterEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_FIT_TO_MAP_AND_CENTER);
  }


  public isMapContextMenuLoadsEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FV_MAP_CONTEXT_MENU_SLB_LOADS_ENABLED);
  }

  public isDriversLiveTabLoadsEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FV_DRIVERS_LIVE_TAB_LOADS_ENABLED);
  }

  public isDriversDedicatedLoadsTabEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FV_DRIVERS_DEDICATED_LOADS_TAB_ENABLED);
  }

  public isVehiclesDedicatedLoadsTabEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FV_VEHICLES_DEDICATED_LOADS_TAB_ENABLED);
  }

  public isFVLiveShareDestinationFeatureEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FV_LIVE_SHARE_DESTINATION_ENABLED);
  }

  public isFVFollowModeLoggingEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_FOLLOW_MODE_LOGGING);
  }

  public isFEFVHardwireChangesEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_HARDWIRED_CHANGES_ENABLED);
  }

  public isFVGeofenceLayerDisabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_GEOFENCES_DISABLED);
  }

  public isDriverProfileHOSCardEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_DRIVER_PROFILE_HOS_CARD_ENABLED);
  }

  public isFVMapMarkerClusteringDisabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_MAP_MARKER_CLUSTERING_DISABLED);
  }

  public isHosClockDisabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_HOS_CLOCK_DISABLED);
  }

  public isATFedexOptimizationEnabled(): boolean {
    return this.isFedexExperienceEnabled;
  }

  public isFedexVehiclePaginatedAPIEnabled(): boolean {
    return (
      (
        this.isFedexExperienceEnabled ||
        this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_FEDEX_VEHICLE_PAGINATED_API_ENABLED)
      ) &&
      this.session.isVehicleFeatureAllowed()
    );
  }

  public isFedexDriverPaginatedAPIEnabled(): boolean {
    return (
      (
        this.isFedexExperienceEnabled ||
        this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_FEDEX_DRIVER_PAGINATED_API_ENABLED)
      ) &&
      this.session.isDriverFeatureAllowed()
    );
  }

  public isFedexAssetPaginatedAPIEnabled(): boolean {
    return (
      this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_FEDEX_ASSET_PAGINATED_API_ENABLED) &&
      this.session.isAssetFeatureAllowed()
    );
  }

  public isHistoryRoutesFallbackForMissingBreadcrumbsEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_HISTORY_ROUTES_FALLBACK_FOR_NO_BREADCRUMBS);
  }

  public isDriverCompanyIdSearchEnabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_DRIVER_COMPANY_ID_SEARCH_ENABLED);
  }

  public isDriverSortOptionDisabled(): boolean {
    return this.statsigService
      .evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_DRIVER_SORT_OPTION_DISABLED);
  }

  public isTripImagesEnabled(): boolean {
    return (this.session.isVideoMonitoringEnabled() || this.session.isImageMonitoringEnabled()) &&
      this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.AT_TRIP_IMAGES_ENABLED);
  }

  public isMarkersMapLayersAndLegendsEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_MARKERS_MAP_LAYERS_AND_LEGENDS);
  }

  public isGroupListEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_SHOW_GROUPS_ON_ENTITY_DETAIL_VIEW);
  }

  public isGeofenceBulkUpdateEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.ASSET_TRACKING_GEOFENCE_BULK_STATUS_UPDATE_ENABLED);
  }

  public isMapInstanceOptimizationEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_MAP_INSTANCE_OPTIMIZATION_ENABLED);
  }

  public isMapLoggingEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_ENABLE_GOOGLE_MAPS_COST_ANALYSIS_LOGS);
  }

  public isTripImagesPhase2Enabled(): boolean {
    return (this.session.isVideoMonitoringEnabled() || this.session.isImageMonitoringEnabled()) &&
      this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_TRIP_IMAGES_PHASE2_ENABLED);
  }

  public isTripHistoryCalendarIndicatorsEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_TRIP_HISTORY_CALENDAR_INDICATORS_ENABLED);
  }

  public isListNearByEntitiesDisabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_DISABLE_LIST_NEARBY_ENTITIES);
  }

  public isShoryukenOmitOutOfOrderPusherUpdatesEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_SHORYUKEN_OMIT_OUT_OF_ORDER_PUSHER_UPDATES);
  }

  public isVehicleDriverHistoryZeroTripEnabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.FE_FV_VEHICLE_DRIVER_HISTORY_ZERO_TRIP);
  }

  public isFleetViewV2Enabled(): boolean {
    return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.AT_FLEETVIEW_V2_M1_ENABLED);
  }

  public isCustomGeofenceCategoriesEnabled(): boolean {
   return this.statsigService.evaluateGate(FLEET_VIEW_FEATURE_FLAGS.AT_CUSTOM_GEOFENCE_CATEGORIES_ENABLED);
  }
}
